import * as React from "react";
import { useEffect, useState } from "react";
import MainApplication from "./MainApplication";
import { Loader2 } from "lucide-react";
import { DocumentMetadataService } from "../features/knowledgebase/services/documentMetadataService";
import { Toaster } from "@/components/ui/sonner";
import { OrganizationProvider, ThemeProvider } from "../providers";

interface AppProps {
  title: string;
}

const App: React.FC<AppProps> = () => {
  const [executionId, setExecutionId] = useState<string | undefined>(undefined);
  const [workpillId, setWorkpillId] = useState<string | undefined>(undefined);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      console.log('🚀 Inicializando aplicación...');

      // 1. Intentar leer metadata del documento primero (tiene prioridad)
      console.log('📄 Leyendo metadata del documento...');
      const documentMetadata = await DocumentMetadataService.readMetadata();

      if (documentMetadata.executionId) {
        console.log('✅ Documento con metadata de ejecución:', documentMetadata);
        setExecutionId(documentMetadata.executionId);
        setWorkpillId(documentMetadata.workpillId);
        setUserId(documentMetadata.userId);
        setIsLoading(false);
        return;
      }

      // 2. Si no hay metadata en documento, intentar leer de URL (deep link)
      console.log('📄 No hay metadata en documento, verificando URL...');
      const urlParams = new URLSearchParams(window.location.search);
      const executionIdFromUrl = urlParams.get('execution_id');
      const workpillIdFromUrl = urlParams.get('workpill_id');
      const userIdFromUrl = urlParams.get('user_id');

      if (executionIdFromUrl) {
        console.log('🔗 Deep link detectado - Execution ID:', executionIdFromUrl);
        setExecutionId(executionIdFromUrl);
        setWorkpillId(workpillIdFromUrl || undefined);
        setUserId(userIdFromUrl || undefined);

        // Guardar en el documento para la próxima vez
        if (executionIdFromUrl) {
          console.log('💾 Guardando metadata en el documento...');
          await DocumentMetadataService.saveMetadata({
            executionId: executionIdFromUrl,
            workpillId: workpillIdFromUrl || undefined,
            userId: userIdFromUrl || undefined
          });
        }
      } else {
        console.log('ℹ️ Modo normal - sin metadata ni parámetros en URL');
      }

    } catch (error) {
      console.error('❌ Error inicializando app:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-slate-50">
        <img 
          src="assets/favicon-galileo-verde.png" 
          alt="WorkOps" 
          className="w-[120px] h-auto animate-pulse"
        />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="m-0 text-slate-500">Loading WorkOps...</p>
      </div>
    );
  }

  return (
    <OrganizationProvider>
      <ThemeProvider>
        <div className="min-h-screen overflow-hidden">
          <MainApplication 
            executionId={executionId}
            workpillId={workpillId}
            userId={userId}
            isDevelopment={!executionId} // Si tiene execution, no es desarrollo
          />
          <Toaster />
        </div>
      </ThemeProvider>
    </OrganizationProvider>
  );
};

export default App;